import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";
import {
  caseStudies,
  heroMetrics,
  homeApproach,
  homeTestimonial,
  homeWorkflow,
  industries,
  siteData,
  solutionPillars,
} from "./siteData";

// Add your banner images to the public/ folder and list them here
const bannerSlides = [
  {
    src: "/banner/banner1.png",
    caption: "Bridging Education & Industry Through Innovative Engineering Solutions.",
    sub: "Software Development, Hardware Prototyping, IT Solutions, & IoT Integration.",
  },
  {
    src: "/banner/banner2.png",
    caption: "Building the Next Generation of Tech Talent.",
    sub: "Real projects. Real mentorship. Real impact.",
  },
  {
    src: "/banner/banner3.png",
    caption: "From Concept to Delivery — We Engineer What Matters.",
    sub: "Agriculture, Manufacturing, Healthcare, Energy, and more.",
  },
];

function PhotoSlider() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % bannerSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = bannerSlides[current];

  return (
    <section className="site-shell mt-4">
      <div className="relative overflow-hidden rounded-[28px] h-[420px] md:h-[520px] bg-slate-900">
        {bannerSlides.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.caption}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: i === current ? 1 : 0 }}
          />
        ))}
        {/* dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-[28px]" />
        {/* caption */}
        <div className="absolute bottom-0 left-0 p-8 md:p-12 max-w-3xl">
          <p className="text-white font-display text-2xl md:text-4xl font-bold leading-tight drop-shadow">
            {slide.caption}
          </p>
          <p className="mt-3 text-white/80 text-sm md:text-base">
            {slide.sub}
          </p>
        </div>
        {/* dot navigation */}
        <div className="absolute bottom-6 right-8 flex gap-2">
          {bannerSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-2 rounded-full transition-all ${
                i === current ? "w-6 bg-white" : "w-2 bg-white/50"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
        {/* prev / next arrows */}
        <button
          onClick={() => setCurrent((current - 1 + bannerSlides.length) % bannerSlides.length)}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition"
          aria-label="Previous slide"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setCurrent((current + 1) % bannerSlides.length)}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition"
          aria-label="Next slide"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <SiteLayout title="Home" description={siteData.description}>
      <PhotoSlider />

      <PageHero
        eyebrow={`${siteData.location} | Software, hardware, AI, and IT systems`}
        title="Engineering Solutions & Talent Development"
        description="SaskTech Lab delivers innovative engineering solutions while developing the next generation of tech talent through mentorship and real-world projects."
        actions={[
          <Link key="primary" to="/contact" className="button-tertiary">
            Submit a Project
          </Link>,
          <Link key="secondary" to="/careers" className="button-secondary">
            Join Our Team
          </Link>,
        ]}
      />

      <section className="site-shell mt-8 pb-4">
        <div className="grid gap-4 md:grid-cols-4">
          {heroMetrics.map((metric) => (
            <div key={metric.label} className="panel-card text-center">
              <p className="font-display text-4xl font-bold text-emerald-900">{metric.value}</p>
              <p className="mt-2 text-sm text-slate-600">{metric.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="site-shell section-space">
        <SectionHeader
          eyebrow="Our Unique Approach"
          title="Not just another engineering company"
          description="We're building a platform where recent graduates gain valuable industry experience while delivering high-quality solutions to our clients. Our dual mission combines professional engineering services with workforce development."
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="panel-card space-y-5">
            {homeApproach.map((paragraph) => (
              <p key={paragraph} className="text-lg leading-8 text-slate-600">
                {paragraph}
              </p>
            ))}
          </div>
          <div className="panel-card">
            <p className="section-tag">Why it works</p>
            <ul className="mt-6 space-y-1 text-sm text-slate-600">
              <li className="list-bullet-item">Clients get real delivery support with experienced oversight.</li>
              <li className="list-bullet-item">Graduates get project experience that translates into employability.</li>
              <li className="list-bullet-item">Mentors shape standards, quality, and long-term capability.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="site-shell section-space">
        <SectionHeader
          eyebrow="Our Services"
          title="Comprehensive engineering solutions"
          description="We provide engineering services across software, hardware, and IT domains — delivered by talented recent graduates working under the guidance of experienced industry mentors."
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {solutionPillars.map((pillar) => (
            <article key={pillar.title} className="panel-card h-full">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{pillar.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{pillar.description}</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                {pillar.bullets.map((bullet) => (
                  <li key={bullet} className="list-bullet-item">{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="site-shell section-space">
        <SectionHeader
          eyebrow="How We Work"
          title="A clear process from intake to delivery"
          description="Every project follows a structured workflow that ensures quality outcomes while creating real growth opportunities for our team."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {homeWorkflow.map((step, index) => (
            <article key={step.title} className="panel-card-gray h-full">
              <p className="font-display text-5xl font-bold text-emerald-200">0{index + 1}</p>
              <h3 className="mt-5 text-xl font-semibold text-emerald-950">{step.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="site-shell section-space">
        <SectionHeader
          eyebrow="Featured Projects"
          title="Work that speaks for itself"
          description="A selection of projects across agriculture, manufacturing, healthcare, and energy that show the range and quality of our engineering work."
        />
        <div className="mt-10 grid gap-6 xl:grid-cols-3">
          {caseStudies.map((study) => (
            <article key={study.title} className="panel-card flex h-full flex-col">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">{study.sector}</p>
              <h3 className="mt-3 font-display text-2xl font-bold text-emerald-950">{study.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{study.summary}</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                {study.results.map((result) => (
                  <li key={result} className="list-bullet-item">{result}</li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-2">
                {study.stack.map((tech) => (
                  <span key={tech} className="chip-tag">
                    {tech}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="site-shell section-space">
        <SectionHeader
          eyebrow="Industries"
          title="Sectors where we deliver real value"
          description="From agriculture and manufacturing to healthcare and the public sector, our teams bring practical engineering expertise to the industries that need it most."
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {industries.map((industry) => (
            <article key={industry.name} className="panel-card-gray">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{industry.name}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{industry.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="site-shell section-space">
        <SectionHeader
          eyebrow="What People Say"
          title="What our clients are saying"
          description="Our mentorship model combines fresh perspectives with proven expertise, resulting in solutions that genuinely serve our clients."
        />
        <div className="mt-10 panel-card max-w-4xl">
          <p className="text-lg leading-8 text-slate-700">"{homeTestimonial.quote}"</p>
          <p className="mt-6 font-semibold text-emerald-950">{homeTestimonial.author}</p>
          <p className="mt-1 text-sm text-slate-500">{homeTestimonial.role}</p>
        </div>
      </section>

      <section className="site-shell section-space pb-10">
        <div className="grid gap-8 rounded-[36px] border border-emerald-900/10 bg-gradient-to-br from-white via-emerald-50 to-emerald-100 p-8 lg:grid-cols-[1.2fr_0.8fr] lg:p-10">
          <div>
            <p className="section-tag">Ready to work with us?</p>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-950">Ready to Work With Us?</h2>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600">
              Whether you need engineering solutions, want to join our talent development program, or want to mentor the next generation of engineers — we'd love to hear from you.
            </p>
          </div>
          <div className="space-y-4">
            <Link to="/contact" className="panel-card block transition hover:-translate-y-0.5">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Submit a project</p>
              <p className="mt-3 text-lg font-semibold text-emerald-950">Submit a Project</p>
            </Link>
            <Link to="/careers" className="panel-card block transition hover:-translate-y-0.5">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Join the team</p>
              <p className="mt-3 text-lg font-semibold text-emerald-950">Explore careers and mentorship</p>
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}